import { createMutation } from '@magicdoorinc/ui';
import { createResource, createSignal, createMemo } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { DEPOSIT_TYPE_DICT } from '~/pages/leases/lease-details/components/lease-deposit-transaction/transactionConfigs';
import { LeaseDepositRepository } from '~/repositories/leaseDepositRepository';
import { LeaseDepositLedgerEntryType } from '~/swagger/portal/Api';
import type {
  AddLeaseDepositDto,
  AddLeaseDepositFilesDto,
  AddLeaseDepositPaymentDto,
  AdjustLeaseDepositDto,
  AdjustLeaseDepositPaymentDto,
  AllocateDepositRequestDto,
  ReleaseDepositRequestDto,
  RemoveLeaseDepositFilesDto,
  UpdateLeaseDepositFilesDto,
} from '~/swagger/portal/Api';
const repo = new LeaseDepositRepository();

export const [LeaseDepositsProvider, useLeaseDeposits] = createMagicDoorContext('LeaseDeposits', () => {
  const [currentId, setCurrentId] = createSignal<string | undefined>(undefined);
  const [deposit, depositActions] = createResource(currentId, (leaseId) => repo.getLeaseDeposit(leaseId));

  const addLeaseDeposit = createMutation(async (leaseId: string, model: AddLeaseDepositDto) => {
    const response = await repo.addLeaseDeposit(leaseId, model);
    depositActions.refetch();
    return response;
  });

  const addLeaseDepositOfflinePayment = createMutation(async (leaseId: string, model: AddLeaseDepositPaymentDto) => {
    const response = await repo.addLeaseDepositOfflinePayment(leaseId, model);

    depositActions.refetch();
    return response;
  });

  const allocateLeaseDeposit = createMutation(async (leaseId: string, model: AllocateDepositRequestDto) => {
    const response = await repo.allocateLeaseDeposit(leaseId, model);
    depositActions.refetch();
    return response;
  });

  const leaseDeposit = createMemo(() => deposit()?.entries ?? []);
  const chartOfAccounts = createMemo(() => deposit()?.chartOfAccounts ?? []);

  const releaseDeposit = createMutation(async (leaseId: string, request: ReleaseDepositRequestDto) => {
    const response = await repo.releaseDeposit(leaseId, request);
    depositActions.refetch();
    return response;
  });

  const deleteDepositBill = createMutation(async (leaseId: string, billId: string) => {
    await repo.deleteDepositBill(leaseId, billId);
    depositActions.refetch();
  });

  const getDepositById = createMutation(async (leaseId: string, depositId: string) => {
    return await repo.getDepositById(leaseId, depositId);
  });

  const adjustDeposit = createMutation(async (leaseId: string, depositId: string, model: AdjustLeaseDepositDto) => {
    const response = await repo.adjustDeposit(leaseId, depositId, model);
    depositActions.refetch();
    return response;
  });

  const addDepositFiles = createMutation(async (leaseId: string, depositId: string, model: AddLeaseDepositFilesDto) => {
    const response = await repo.addDepositFiles(leaseId, depositId, model);
    depositActions.refetch();
    return response;
  });

  const uploadDepositFiles = createMutation(async (leaseId: string, depositId: string, files: File[]) => {
    return await repo.uploadDepositFiles(leaseId, depositId, files);
  });

  const updateDepositFiles = createMutation(async (leaseId: string, depositId: string, model: UpdateLeaseDepositFilesDto) => {
    const response = await repo.updateDepositFiles(leaseId, depositId, model);
    depositActions.refetch();
    return response;
  });

  const removeDepositFiles = createMutation(async (leaseId: string, depositId: string, model: RemoveLeaseDepositFilesDto) => {
    const response = await repo.removeDepositFiles(leaseId, depositId, model);
    depositActions.refetch();
    return response;
  });

  const getDepositPayment = createMutation(async (leaseId: string, paymentId: string) => {
    return await repo.getDepositPayment(leaseId, paymentId);
  });

  const adjustDepositPayment = createMutation(async (leaseId: string, paymentId: string, model: AdjustLeaseDepositPaymentDto) => {
    const response = await repo.adjustDepositPayment(leaseId, paymentId, model);
    depositActions.refetch();
    return response;
  });

  const deleteDepositPayment = createMutation(async (leaseId: string, paymentId: string) => {
    await repo.deleteDepositPayment(leaseId, paymentId);
    depositActions.refetch();
  });

  const getDepositTransfer = createMutation(async (leaseId: string, transferId: string) => {
    return await repo.getDepositTransfer(leaseId, transferId);
  });

  const deleteDepositTransfer = createMutation(async (leaseId: string, transferId: string) => {
    await repo.deleteDepositTransfer(leaseId, transferId);
    depositActions.refetch();
  });

  const getDepositTransaction = createMutation(
    async (params: { leaseId: string; transactionId: string; type: LeaseDepositLedgerEntryType }) => {
      const { leaseId, transactionId, type } = params;
      switch (type) {
        case LeaseDepositLedgerEntryType.Payment:
        case LeaseDepositLedgerEntryType.Return:
        case LeaseDepositLedgerEntryType.Payout:
          return await repo.getDepositPayment(leaseId, transactionId);
        case LeaseDepositLedgerEntryType.Transfer:
          return await repo.getDepositTransfer(leaseId, transactionId);
        case LeaseDepositLedgerEntryType.Deposit:
          return await repo.getDepositById(leaseId, transactionId);
        default:
          return undefined;
      }
    }
  );

  const deleteStrategiesDict = DEPOSIT_TYPE_DICT.cloneAndExtend((item) => {
    const deleteFnMap: Record<LeaseDepositLedgerEntryType, (leaseId: string, id: string) => Promise<void>> = {
      [LeaseDepositLedgerEntryType.Deposit]: async (leaseId, id) => deleteDepositBill(leaseId, id),
      [LeaseDepositLedgerEntryType.Payment]: async (leaseId, id) => deleteDepositPayment(leaseId, id),
      [LeaseDepositLedgerEntryType.Return]: async (leaseId, id) => deleteDepositPayment(leaseId, id),
      [LeaseDepositLedgerEntryType.Transfer]: async (leaseId, id) => deleteDepositTransfer(leaseId, id),
      [LeaseDepositLedgerEntryType.Payout]: async (leaseId, id) => deleteDepositPayment(leaseId, id),
    };
    item.meta!.deleteFn = deleteFnMap[item.value];
    return item;
  });

  const deleteByType = async (leaseId: string, type: LeaseDepositLedgerEntryType, id: string) => {
    const meta = deleteStrategiesDict.getMetaByValue(type);
    if (!meta?.deleteFn) {
      throw new Error(`Unknown deposit type: ${type}`);
    }
    await meta.deleteFn(leaseId, id);
  };

  const getTypeLabelByValue = (type: LeaseDepositLedgerEntryType | undefined) => {
    return deleteStrategiesDict.getLabelByValue(type);
  };

  return {
    deposit,
    setCurrentId,
    addLeaseDepositOfflinePayment,
    addLeaseDeposit,
    releaseDeposit,
    allocateLeaseDeposit,
    leaseDeposit,
    chartOfAccounts,
    depositActions,
    deleteDepositBill,
    getDepositById,
    adjustDeposit,
    addDepositFiles,
    uploadDepositFiles,
    updateDepositFiles,
    removeDepositFiles,
    getDepositPayment,
    adjustDepositPayment,
    deleteDepositPayment,
    getDepositTransfer,
    deleteDepositTransfer,
    getDepositTransaction,
    deleteByType,
    getTypeLabelByValue,
  };
});
